import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { useRecoilValue } from 'recoil';

import { hourlyWeatherPageTestIDs, Theme } from '@constants';
import { useGetUserLocation, useMyWeatherHourlyForecast } from '@hooks';
import { dayDateTimeState, hourlyWeatherPageFocusIndex } from '@store';

import { TimeUtils } from '../../../helpers/TimeUtils';
import { TWCAccessibleContent } from '../../focusable/TWCAccessibleContent/TWCAccessibleContent';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'hourlyWeatherPage.hourlyWeatherHeader';
const testIDPath = hourlyWeatherPageTestIDs;

export const HourlyWeatherHeader = ({ nextFocusDown, defaultFocus }) => {
  const { t } = useTranslation('translation', { keyPrefix: tPath });
  const { city } = useGetUserLocation();
  const { params } = useRoute();
  const forecasts = useMyWeatherHourlyForecast(params?.validTimeUtc);
  const focusIndex = useRecoilValue(hourlyWeatherPageFocusIndex);
  const { timeZone } = useRecoilValue(dayDateTimeState);

  const date = forecasts[focusIndex]?.validTimeUtc
    ? TimeUtils.dateFromEpoch(forecasts[focusIndex].validTimeUtc)
    : Date.prototype.getCurrent();
  const dateString = TimeUtils.getWeekDayMonthDate(date, timeZone);

  return (
    <TWCAccessibleContent
      accessibilityHint={t('accessibilityHint')}
      accessibilityLabel={t('accessibilityLabel', {
        city,
        date: dateString,
      })}
      disabledScreenreaderFocus={defaultFocus}
      nextFocusDown={nextFocusDown}
      nextFocusLeft={null}
      nextFocusRight={null}
      nextFocusUp={null}
      testID={hourlyWeatherPageTestIDs.hourlyWeatherTopRow}
    >
      <View importantForAccessibility={'no-hide-descendants'} style={styles.container}>
        <View style={styles.topRow}>
          <TWCText testID={testIDPath.hourlyWeatherHeaderLabel} variant={'pageHeader'}>
            {t('headerLabel')}
          </TWCText>
        </View>
        <View style={styles.bottomRow}>
          <TWCText testID={testIDPath.hourlyWeatherSubheaderLabel} variant={'regularText'}>
            {city}
          </TWCText>
          <View style={styles.divider} />
          <TWCText testID={testIDPath.hourlyWeatherHeaderDate} variant={'regularText'}>
            {dateString}
          </TWCText>
        </View>
      </View>
    </TWCAccessibleContent>
  );
};

const styles = StyleSheet.create({
  bottomRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  container: {
    flexDirection: 'column',
    height: Theme.px(120),
    width: Theme.px(1420),
  },
  divider: {
    backgroundColor: Theme.whisperWhite,
    height: Theme.px(28),
    marginLeft: Theme.px(20),
    marginRight: Theme.px(20),
    marginTop: Theme.px(6),
    width: Theme.px(2),
  },
  topRow: {
    alignItems: 'center',
    flexDirection: 'row',
    height: Theme.px(77),
    justifyContent: 'flex-start',
    marginBottom: Theme.px(8),
    width: '100%',
  },
});
